import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'react-redux';
import { DiagnosticSeverity } from 'vscode-languageserver-protocol';
import { DUPLICATE_DRIVE_BASE_MESSAGE_DEFAULT, mergeDuplicateDriveBaseDiagnostics } from '../../Components/CodeEditor/utils/driveBaseSingleton';
import {
  LINEAR_MECHANISM_VELOCITY_LINT_MESSAGE_DEFAULTS,
  LinearMechanismVelocityLintMessages,
  mergeLinearMechanismVelocityDiagnostics,
} from '../../Components/CodeEditor/utils/linearMechanismVelocityLint';
import {
  LIFECYCLE_LINT_MESSAGE_DEFAULTS,
  LifecycleLintMessages,
  mergeLifecycleDiagnostics,
} from '../../Components/CodeEditor/utils/programLifecycleLint';
import { projectSlice } from '../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import type { RootState } from '../../redux/Store';
import { ensureProgramLspClientDisposedOnUnload, programLspClient } from './programLspClient';

const LINTER_DIAGNOSTICS_TIMEOUT_MS = 5000;

function withLocalDiagnostics(
  code: string,
  diagnostics: Parameters<typeof mergeDuplicateDriveBaseDiagnostics>[1],
  duplicateDriveBaseMessage: string,
  lifecycleMessages: LifecycleLintMessages,
  linearMechanismVelocityMessages: LinearMechanismVelocityLintMessages
) {
  const withDriveBase = mergeDuplicateDriveBaseDiagnostics(code, diagnostics, duplicateDriveBaseMessage);
  const withLifecycle = mergeLifecycleDiagnostics(code, withDriveBase, lifecycleMessages);
  return mergeLinearMechanismVelocityDiagnostics(code, withLifecycle, linearMechanismVelocityMessages);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function createPyrightServiceErrorDiagnostic(errorMessage: string, detail: string) {
  return {
    message: `${errorMessage}\n    ${detail}`,
    severity: DiagnosticSeverity.Error,
    range: {
      start: { line: 0, character: 0 },
      end: { line: 0, character: 0 },
    },
  };
}

export function useProgramDiagnosticsSync(): void {
  const project = useAppSelector((state) => state.project.loadedProject);
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { t } = useTranslation();
  const duplicateDriveBaseMessage = t('editor.duplicateDriveBaseDiagnostic.message', DUPLICATE_DRIVE_BASE_MESSAGE_DEFAULT);
  const duplicateDriveBaseMessageRef = useRef(duplicateDriveBaseMessage);
  const lifecycleMessages = useMemo<LifecycleLintMessages>(
    () => ({
      loopBlocksStreamingAndStart: t('editor.lifecycleLint.loopBlocksStreamingAndStart', LIFECYCLE_LINT_MESSAGE_DEFAULTS.loopBlocksStreamingAndStart),
      loopBlocksStart: t('editor.lifecycleLint.loopBlocksStart', LIFECYCLE_LINT_MESSAGE_DEFAULTS.loopBlocksStart),
      loopMayDelayStreamingAndStart: t(
        'editor.lifecycleLint.loopMayDelayStreamingAndStart',
        LIFECYCLE_LINT_MESSAGE_DEFAULTS.loopMayDelayStreamingAndStart
      ),
      loopMayDelayStart: t('editor.lifecycleLint.loopMayDelayStart', LIFECYCLE_LINT_MESSAGE_DEFAULTS.loopMayDelayStart),
      loopBlocksStreaming: t('editor.lifecycleLint.loopBlocksStreaming', LIFECYCLE_LINT_MESSAGE_DEFAULTS.loopBlocksStreaming),
      loopMayDelayStreaming: t('editor.lifecycleLint.loopMayDelayStreaming', LIFECYCLE_LINT_MESSAGE_DEFAULTS.loopMayDelayStreaming),
      blockingCallBeforeStreamingAndStart: t(
        'editor.lifecycleLint.blockingCallBeforeStreamingAndStart',
        LIFECYCLE_LINT_MESSAGE_DEFAULTS.blockingCallBeforeStreamingAndStart
      ),
      blockingCallBeforeStart: t('editor.lifecycleLint.blockingCallBeforeStart', LIFECYCLE_LINT_MESSAGE_DEFAULTS.blockingCallBeforeStart),
      blockingCallBeforeStreaming: t('editor.lifecycleLint.blockingCallBeforeStreaming', LIFECYCLE_LINT_MESSAGE_DEFAULTS.blockingCallBeforeStreaming),
      hardwareAfterStreaming: t('editor.lifecycleLint.hardwareAfterStreaming', LIFECYCLE_LINT_MESSAGE_DEFAULTS.hardwareAfterStreaming),
      hardwareAfterStart: t('editor.lifecycleLint.hardwareAfterStart', LIFECYCLE_LINT_MESSAGE_DEFAULTS.hardwareAfterStart),
      streamingAfterStart: t('editor.lifecycleLint.streamingAfterStart', LIFECYCLE_LINT_MESSAGE_DEFAULTS.streamingAfterStart),
    }),
    [t]
  );
  const lifecycleMessagesRef = useRef(lifecycleMessages);
  const linearMechanismVelocityMessages = useMemo<LinearMechanismVelocityLintMessages>(
    () => ({
      motorRequiresRotational: t(
        'editor.linearMechanismVelocityLint.motorRequiresRotational',
        LINEAR_MECHANISM_VELOCITY_LINT_MESSAGE_DEFAULTS.motorRequiresRotational
      ),
      mechanismOutputRequiresLinear: t(
        'editor.linearMechanismVelocityLint.mechanismOutputRequiresLinear',
        LINEAR_MECHANISM_VELOCITY_LINT_MESSAGE_DEFAULTS.mechanismOutputRequiresLinear
      ),
    }),
    [t]
  );
  const linearMechanismVelocityMessagesRef = useRef(linearMechanismVelocityMessages);
  const latestCodeRef = useRef('');
  const diagnosticsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const diagnosticsRequestIdRef = useRef(0);

  const clearDiagnosticsTimeout = useCallback(() => {
    if (diagnosticsTimeoutRef.current === null) return;
    clearTimeout(diagnosticsTimeoutRef.current);
    diagnosticsTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    duplicateDriveBaseMessageRef.current = duplicateDriveBaseMessage;
  }, [duplicateDriveBaseMessage]);

  useEffect(() => {
    lifecycleMessagesRef.current = lifecycleMessages;
  }, [lifecycleMessages]);

  useEffect(() => {
    linearMechanismVelocityMessagesRef.current = linearMechanismVelocityMessages;
  }, [linearMechanismVelocityMessages]);

  useEffect(() => {
    latestCodeRef.current = project?.program.code ?? '';
  }, [project?.program.code]);

  useEffect(() => {
    ensureProgramLspClientDisposedOnUnload();
  }, []);

  useEffect(() => {
    programLspClient.requestNotification({
      onDiagnostics: ({ diagnostics, programCode }) => {
        const currentCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
        if (programCode !== currentCode) return;

        clearDiagnosticsTimeout();
        const nextDiagnostics = withLocalDiagnostics(
          programCode,
          diagnostics,
          duplicateDriveBaseMessageRef.current,
          lifecycleMessagesRef.current,
          linearMechanismVelocityMessagesRef.current
        );
        dispatch(projectSlice.actions.setDiagnostics({ diagnostics: nextDiagnostics, programCode }));
      },
      onError: (message: string) => {
        const programCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
        if (!programCode) return;

        clearDiagnosticsTimeout();
        const errorMessage = t('editor.error.pyright.web.service.error', 'An error occurred when attempting to contact the pyright web service');
        const errorDiagnostic = createPyrightServiceErrorDiagnostic(errorMessage, message);
        // Keep locally computable diagnostics (lifecycle, duplicate DriveBase) visible
        // even when pyright fails — the user decides whether to ignore the failure.
        const nextDiagnostics = withLocalDiagnostics(
          programCode,
          [errorDiagnostic],
          duplicateDriveBaseMessageRef.current,
          lifecycleMessagesRef.current,
          linearMechanismVelocityMessagesRef.current
        );
        dispatch(projectSlice.actions.setDiagnostics({ diagnostics: nextDiagnostics, programCode }));
      },
      onWaitingForInitialization: (isWaiting) => {
        dispatch(projectSlice.actions.setLinterDiagnosticsPending(isWaiting));
      },
    });
  }, [clearDiagnosticsTimeout, dispatch, duplicateDriveBaseMessage, project?.program.code, store, t]);

  useEffect(
    () => () => {
      clearDiagnosticsTimeout();
    },
    [clearDiagnosticsTimeout]
  );

  useEffect(() => {
    const code = project?.program.code ?? '';
    if (!project) return;

    programLspClient.updateCode(code);
    diagnosticsRequestIdRef.current += 1;
    clearDiagnosticsTimeout();

    if (code === '') {
      dispatch(projectSlice.actions.setDiagnostics({ diagnostics: [], programCode: code }));
      return;
    }

    const requestId = diagnosticsRequestIdRef.current;
    dispatch(projectSlice.actions.setLinterDiagnosticsPending(true));
    diagnosticsTimeoutRef.current = setTimeout(() => {
      if (diagnosticsRequestIdRef.current !== requestId) return;
      diagnosticsTimeoutRef.current = null;

      const currentCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
      if (code !== currentCode) return;

      const errorMessage = t('editor.error.pyright.web.service.error', 'An error occurred when attempting to contact the pyright web service');
      const timeoutMessage = t('editor.error.pyright.web.service.timeout', 'Timed out waiting for pyright diagnostics');
      const errorDiagnostic = createPyrightServiceErrorDiagnostic(errorMessage, timeoutMessage);
      const nextDiagnostics = withLocalDiagnostics(
        code,
        [errorDiagnostic],
        duplicateDriveBaseMessageRef.current,
        lifecycleMessagesRef.current,
        linearMechanismVelocityMessagesRef.current
      );
      dispatch(projectSlice.actions.setDiagnostics({ diagnostics: nextDiagnostics, programCode: code }));
    }, LINTER_DIAGNOSTICS_TIMEOUT_MS);

    void Promise.resolve(programLspClient.updateTextDocument(code)).catch((error: unknown) => {
      const currentCode = store.getState().project.loadedProject?.program.code ?? latestCodeRef.current;
      if (code !== currentCode) return;

      clearDiagnosticsTimeout();
      const errorMessage = t('editor.error.pyright.web.service.error', 'An error occurred when attempting to contact the pyright web service');
      const errorDiagnostic = createPyrightServiceErrorDiagnostic(errorMessage, getErrorMessage(error));
      const nextDiagnostics = withLocalDiagnostics(
        code,
        [errorDiagnostic],
        duplicateDriveBaseMessageRef.current,
        lifecycleMessagesRef.current,
        linearMechanismVelocityMessagesRef.current
      );
      dispatch(projectSlice.actions.setDiagnostics({ diagnostics: nextDiagnostics, programCode: code }));
    });
  }, [clearDiagnosticsTimeout, dispatch, project?.id, project?.program.code, project, store, t]);
}
